/**
 * Formats a number according to the user's browser locale.
 * e.g., 1000 becomes "1,000" in en-US or "1 000" in fr-FR.
 * @param num The number to format.
 * @returns A locale-specific formatted number string.
 */
export const formatNumber = (num: number): string => {
    try {
        return new Intl.NumberFormat(navigator.language).format(num);
    } catch (e) {
        // Fallback for environments where navigator.language is not available
        return num.toString();
    }
};

/**
 * Converts a duration from seconds into a human-readable format (e.g., 1h 23m 45s).
 * It rounds the seconds to the nearest whole number before formatting.
 * @param totalSeconds The duration in seconds.
 * @returns A formatted string representing the duration.
 */
export const formatDuration = (totalSeconds: number): string => {
    if (totalSeconds < 0) {
        return "0s";
    }

    // Round to the nearest second to avoid floating point issues
    const roundedTotalSeconds = Math.round(totalSeconds);

    const hours = Math.floor(roundedTotalSeconds / 3600);
    const minutes = Math.floor((roundedTotalSeconds % 3600) / 60);
    const seconds = roundedTotalSeconds % 60;

    let result = '';
    if (hours > 0) {
        result += `${formatNumber(hours)}h `;
    }
    if (minutes > 0) {
        result += `${minutes}m `;
    }
    // Also show seconds if they are the only unit, even if 0
    if (seconds > 0 || result === '') {
        result += `${seconds}s`;
    }

    return result.trim();
};
